import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Star, ChevronDown, Check } from 'lucide-react'
import talentFavoritesService from '../../services/talentFavoritesService'
import { users } from '../../services/apiService'
import { useFavorites } from '../../context/FavoritesContext'
import { getAnchoredOverlayStyle, isRectOutsideViewport } from '../../utils/viewportPosition'

/**
 * Фильтр «избранное» для любой страницы со списком талантов.
 *
 * Один контрол вместо двух селектов: сначала выбирается чьё избранное,
 * затем — какие его списки. Списки принадлежат владельцу, поэтому при выборе
 * чужого избранного подгружаются ЕГО списки: свои дали бы заведомо пустой
 * результат (и 403 на чужой list_id).
 *
 * Оформлен как SavedFiltersDropdown — те же классы триггера и панели.
 */
const FavoritesFilterDropdown = ({
  favoritesOf = '',
  favoriteListIds = [],
  onChange,
  currentUserId,
  canReadForeign = false,
  className = '',
}) => {
  const { lists: myLists } = useFavorites()
  const [open, setOpen] = useState(false)
  const [owners, setOwners] = useState([])
  const [ownersLoaded, setOwnersLoaded] = useState(false)
  const [foreignLists, setForeignLists] = useState([])
  const [loadingLists, setLoadingLists] = useState(false)
  const wrapRef = useRef(null)
  const dropdownRef = useRef(null)
  const [dropdownStyle, setDropdownStyle] = useState(null)

  const myId = currentUserId ? String(currentUserId) : ''
  const selectedOwner = favoritesOf ? String(favoritesOf) : ''
  const isForeign = !!selectedOwner && selectedOwner !== myId
  const lists = isForeign ? foreignLists : myLists

  useEffect(() => {
    if (!open) return
    const onMouseDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)
        && dropdownRef.current && !dropdownRef.current.contains(event.target)) setOpen(false)
    }
    const onKeyDown = (event) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const updateDropdownPosition = useCallback(() => {
    if (!open || !wrapRef.current) return
    const anchorRect = wrapRef.current.getBoundingClientRect()
    const viewport = { viewportWidth: window.innerWidth, viewportHeight: window.innerHeight }
    if (isRectOutsideViewport(anchorRect, viewport)) {
      setOpen(false)
      return
    }
    setDropdownStyle(getAnchoredOverlayStyle({
      anchorRect,
      ...viewport,
      preferredWidth: 288,
      preferredHeight: 420,
      align: 'end',
    }))
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    updateDropdownPosition()
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [open, updateDropdownPosition])

  // Список владельцев тянем лениво: при открытии панели либо когда чужой
  // владелец уже выбран (нужно его имя для подписи на кнопке).
  useEffect(() => {
    if (!canReadForeign || ownersLoaded) return
    if (!open && !isForeign) return
    let cancelled = false
    Promise.all([talentFavoritesService.owners(), users.getAllUsers()])
      .then(([ownersResponse, usersResponse]) => {
        if (cancelled) return
        const rows = ownersResponse?.data || []
        const allUsers = usersResponse?.success
          ? (Array.isArray(usersResponse.data) ? usersResponse.data : usersResponse.data?.users || [])
          : []
        const byId = new Map(allUsers.map(u => [String(u.id), u]))
        setOwners(rows
          .filter(row => String(row.user_id) !== myId)
          .map(row => {
            const found = byId.get(String(row.user_id))
            return {
              id: String(row.user_id),
              name: found?.name || found?.email || String(row.user_id),
              items_count: row.items_count,
            }
          }))
        setOwnersLoaded(true)
      })
      .catch(error => console.error('Failed to load favorite owners:', error))
    return () => { cancelled = true }
  }, [canReadForeign, ownersLoaded, open, isForeign, myId])

  useEffect(() => {
    if (!isForeign) {
      setForeignLists([])
      return
    }
    let cancelled = false
    setLoadingLists(true)
    talentFavoritesService.listLists(selectedOwner)
      .then(response => { if (!cancelled) setForeignLists(response?.data || []) })
      .catch(error => {
        if (!cancelled) {
          console.error('Failed to load favorite lists:', error)
          setForeignLists([])
        }
      })
      .finally(() => { if (!cancelled) setLoadingLists(false) })
    return () => { cancelled = true }
  }, [isForeign, selectedOwner])

  const ownerName = useMemo(() => {
    if (!selectedOwner) return null
    if (!isForeign) return 'My favorites'
    return owners.find(o => o.id === selectedOwner)?.name || 'Favorites'
  }, [selectedOwner, isForeign, owners])

  const triggerLabel = useMemo(() => {
    if (!selectedOwner) return 'Favorites'
    const listNames = lists
      .filter(list => favoriteListIds.map(Number).includes(list.id))
      .map(list => list.name)
    if (listNames.length === 1) return `${ownerName}: ${listNames[0]}`
    if (listNames.length > 1) return `${ownerName}: ${listNames.length} lists`
    return ownerName
  }, [selectedOwner, lists, favoriteListIds, ownerName])

  const selectOwner = (ownerId) => {
    // список прежнего владельца новому не принадлежит
    onChange({ favorites_of: ownerId, favorite_list_id: [] })
  }

  const toggleList = (listId) => {
    const current = favoriteListIds.map(Number)
    const next = current.includes(listId)
      ? current.filter(id => id !== listId)
      : [...current, listId]
    onChange({ favorites_of: selectedOwner || myId, favorite_list_id: next })
  }

  const ownerRow = (id, label, hint, active) => (
    <button
      key={id || 'anyone'}
      type="button"
      onClick={() => selectOwner(id)}
      className={`min-h-10 w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
        active ? 'bg-indigo-50 dark:bg-indigo-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      <span className="w-4 shrink-0 text-indigo-600 dark:text-indigo-400">
        {active && <Check className="h-4 w-4" />}
      </span>
      <span className="truncate text-gray-700 dark:text-gray-200">{label}</span>
      {hint !== null && hint !== undefined && (
        <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400">{hint}</span>
      )}
    </button>
  )

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex min-h-10 items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <Star className={`h-4 w-4 ${selectedOwner ? 'text-amber-500' : ''}`} fill={selectedOwner ? 'currentColor' : 'none'} />
        <span className="max-w-[160px] truncate">{triggerLabel}</span>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </button>

      {open && dropdownStyle && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="flex min-h-0 flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="px-3 pt-2 pb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              Whose favorites
            </div>
            {ownerRow('', 'Anyone (no filter)', null, !selectedOwner)}
            {ownerRow(myId, 'Me', null, !!selectedOwner && !isForeign)}
            {canReadForeign && owners.map(owner =>
              ownerRow(owner.id, owner.name, owner.items_count, selectedOwner === owner.id)
            )}
            {canReadForeign && ownersLoaded && owners.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                Nobody else has favorites yet
              </div>
            )}

            {selectedOwner && (
              <>
                <div className="px-3 pt-3 pb-1 text-xs font-medium text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 mt-1">
                  Lists
                </div>
                {loadingLists && (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
                )}
                {!loadingLists && lists.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">No lists</div>
                )}
                {!loadingLists && lists.map(list => {
                  const checked = favoriteListIds.map(Number).includes(list.id)
                  return (
                    <button
                      key={list.id}
                      type="button"
                      onClick={() => toggleList(list.id)}
                      className="min-h-10 w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <span className={`w-4 h-4 shrink-0 flex items-center justify-center rounded border ${
                        checked
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate text-gray-700 dark:text-gray-200">{list.name}</span>
                      <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400">
                        {list.items_count ?? 0}
                      </span>
                    </button>
                  )
                })}
                <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400">
                  No list selected — all favorites of {ownerName}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default FavoritesFilterDropdown
